export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(" ");
}

export function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export * from "./throttle";
export * from "./array-move";

export const replace_keys_and_values_in_object = <T>(value: T, replacer: (v: any) => any): T => {
  const replacement = replacer(value);
  if (replacement !== value) return replacement as T;

  if (Array.isArray(value)) {
    return [...value].map((v) => replace_keys_and_values_in_object(v, replacer)) as T;
  }
  // make sure it's a basic object (constructor === Object) and not a Class, Date, etc.
  if (value && typeof value === "object" && value.constructor === Object) {
    // don't deep copy, so that we replace keys rather than adding new keys
    const newValue = {} as Record<string, any>;
    for (const k in value) {
      newValue[replace_keys_and_values_in_object(k, replacer)] = replace_keys_and_values_in_object(value[k], replacer);
    }
    return newValue as T;
  }

  return value;
};
// finds any keys or values which match the keys in 'mapping' and replaces them the corresponding value in mapping[key]
export const replace_keys_and_values_in_object_for_string_mapping = <T>(value: T, mapping: Record<string, string>): T =>
  replace_keys_and_values_in_object(value, (v) => (typeof v === "string" && mapping[v] ? mapping[v] : v));

// Enhanced version that supports both exact matches and partial string replacements within larger strings
export const replace_keys_and_values_in_object_for_string_mapping_with_partials = <T>(
  value: T,
  mapping: Record<string, string>
): T =>
  replace_keys_and_values_in_object(value, (v) => {
    if (typeof v !== "string") return v;

    // First try exact match for efficiency
    if (mapping[v]) return mapping[v];

    // Then check for partial matches within the string
    let result = v;
    for (const [sourceId, targetId] of Object.entries(mapping)) {
      if (result.includes(sourceId)) {
        // Replace all instances of sourceId with targetId
        result = result.replaceAll(sourceId, targetId);
      }
    }

    return result;
  });

// Export environment utilities
export * from "./environment";

// Export storage utilities
export * from "./storage";

// Export MongoDB filter utilities
export * from "./mongodb-filter";
export * from "./template";

// Export freeform filter model + compile helpers (shared reusable filter builder)
export * from "./freeform-filter";

// Export SDK pagination utilities
export * from "./sdk-pagination";

// Export object comparison and diff utilities
export * from "./object-diff";

// Export SDK model update diffing
export * from "./model-update-diff";

// Export array batching utilities
export * from "./array-batching";

// Export shared constants
export * from "./constants";

export * from "./get-currency-symbol";

export * from "./resolve-timezone";

export * from "./date-formatters";

export * from "./get-date-fns-locale";

export * from "./get-iso-639-1-code";

export * from "./date-arithmetic";

export * from "./address-formatters";

export * from "./phone-formatters";

export * from "./csv-utils";

export * from "./parse-timestamp";

export * from "./parse-date-input";

export * from "./mask-date-input";

export * from "./validators";

export * from "./attachment-utils";

export * from "./name-formatters";

export * from "./duration-formatters";

export * from "./get-age";

export * from "./levenshtein-distance";

export * from "./url-utils";
export * from "./format-stripe-product-cost";

export * from "./format-stripe-subscription-interval";

export * from "./format-currency";

export * from "./currency-units";

export * from "./format-card-brand";

export * from "./format-file-size";

export * from "./convert-heic";

export * from "./slugify";

export * from "./snake-case-to-title-case";

export * from "./email";

export * from "./html";

export * from "./create-id-map";
export * from "./create-key-map";

export * from "./color-utils";
export * from "./deep-merge";
export * from "./mdb-filter-utils";

export * from "./reduce-to-values-for-key";

export * from "./get-nested-field-value";

export * from "./format-file-inline-template-string";

export * from "./format-content-inline-template-string";

export * from "./get-contrast-ratio";
export * from "./get-deterministic-hex-color";
export * from "./get-avatar-background";
export * from "./get-relative-luminance";
export * from "./hash-string";
export * from "./hex-to-rgb";
export * from "./hsl-to-hex";
export * from "./hsl-to-rgb";
export * from "./lighten-hex";
export * from "./normalize-to-hex-color";
export * from "./rgb-to-hex";
export * from "./parse-vital-program-configuration-value";
export * from "./calendar-utils";
export * from "./discriminated-string-union-includes";
export * from "./get-observation-flags";
export * from "./get-observation-type";
export * from "./observation-classifications";
export * from "./get-enduser-timeline-ai-summary-metadata";
export * from "./parse-call-recording-transcription-data";
export * from "./render-email-for-forwarded-message";
export type * from "./types";
export * from "./normalize-twilio-phone";
export * from "./get-sms-parts";
